import React, { useContext } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Form, Grid, Header, Segment } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmCropOptionsContext } from '../../index'
import { useTranslation } from 'react-i18next'
import InfoPopup from '~/components/InfoPopup'
import CoProducts from './CoProducts'
import { YearInput } from 'semantic-ui-calendar-react'
import dayjs from 'dayjs'

const CropDetails: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const options = useContext(CoolFarmCropOptionsContext)

  const cropTypeOptions = options?.cropTypes?.map(type => ({
    key: type.id,
    value: type.id,
    text: type.label
  })) || []

  const cropHectarUnits = options?.cropHarvestingUnits?.map(unit => ({
    key: unit.key,
    value: unit.key,
    text: unit.label
  })) || []

  const residueManagementOptions = options?.cropResidueManagementTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const hectarUnitOptions = options?.cropPerHectarUnits?.map(unit => ({
    key: unit.key,
    value: unit.key,
    text: unit.label
  })) || []

  const currentYear = dayjs().year()

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Crop details')}
              <InfoPopup
                style={{ minWidth: 400 }}
                content={t(`Enter information for the crop and field you are assessing. Choose the boundaries of your assessment carefully:
              Firstly, the start and end date of your assessment should be chosen to include all inputs and activities in the preparation as well as the sowing / planting / growing / harvesting of your crop, and also in the post harvest phase.
              Secondly, your assessment should be of a single crop type and single crop cycle, and of a discrete field or managed unit.
              Finally, it is your choice where you start and end your assessment in the chain. For example, most assessments include transport of incoming goods and stop at 'farm gate', but you may wish to also include transport of final product to your customer, and any off farm storage or processing.
              You should make User notes in the assessment as you go; ensure you keep consistency between multiple assessments; and consider disclosing this information if you are sharing your assessment. For more information, see the online User Guide and Data Input Guide.`)}
                styleIcon={{ fontSize: '1.3em' }}
              />
            </div>
          </Header>
          <Segment attached>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FormFields.Select
                    control={control}
                    search
                    name='cftCropTypeId'
                    label={<div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      <label>{t('Crop name')}</label>
                      <InfoPopup
                        content={t(`If your specific crop is not listed, choose either the most similar crop or the relevant 'other' crop type from the dropdown list. 
                        This will not reduce the accuracy of the assessment, 
                        but means you will need to provide your own residue estimate in section 1.2. The crop selection affects residue amounts, below-ground biomass, and nitrous oxide emissions from residues.`)}
                        styleIcon={{ fontSize: '1.2em' }}
                      />
                    </div>}
                    withoutAsterisk
                    options={cropTypeOptions}
                    rules={{ required: true }}
                    placeholder={t('Select crop type')}
                    noErrorMessage
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <Controller
                    name='harvestingYear'
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => {
                      return <Form.Field>
                        <label>{t('Harvest year')}</label>
                        <YearInput
                          name='date'
                          autoComplete='off'
                          closable
                          clearIcon={'close'}
                          minDate={'1980'}
                          onKeyDown={handleKeyPress}
                          maxDate={currentYear.toString()}
                          clearable
                          closeOnMouseLeave={false}
                          placeholder={t('Enter harvest year')}
                          className={'datePicker'}
                          value={field.value}
                          iconPosition='left'
                          onChange={(event, { name, value }) => setValue('harvestingYear', value)}
                        />
                      </Form.Field>
                    }}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <FormFields.Input
                    control={control}
                    name='cropArea'
                    withoutAsterisk
                    label={t('Crop area')}
                    suffix=' hectar'
                    rules={{ required: true }}
                    placeholder={t('in hectar')}
                    typeInput='controled'
                    noErrorMessage
                  />
                </Grid.Column>
                {/*   <Grid.Column width={8}>
                  <FormFields.Select
                    control={control}
                    name='amountUnit'
                    label={t('Amount unit')}
                    withoutAsterisk
                    options={cropPerHectarUnits}
                    rules={{ required: true }}
                    placeholder={t('Select unit')}
                    noErrorMessage
                  />
                </Grid.Column> */}
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <FormFields.Input
                    control={control}
                    name='freshAmount'
                    withoutAsterisk
                    rules={{ required: true }}
                    label={t('Harvested amount (total)')}
                    placeholder={t('Enter Harvested amount')}
                    typeInput='controled'
                    noErrorMessage
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FormFields.Select
                    control={control}
                    name='amountUnit'
                    label={t('Amount unit')}
                    withoutAsterisk
                    options={cropHectarUnits}
                    rules={{ required: true }}
                    placeholder={t('Select unit')}
                    noErrorMessage
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FormFields.Input
                    control={control}
                    name='dryAmount'
                    withoutAsterisk
                    rules={{ required: true }}
                    label={t('Farm-gate ready amount')}
                    placeholder={t('Enter Farm-gate amount')}
                    typeInput='controled'
                    noErrorMessage
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FormFields.Select
                    control={control}
                    name='amountUnit'
                    label={t('Amount unit')}
                    withoutAsterisk
                    options={cropHectarUnits}
                    rules={{ required: true }}
                    placeholder={t('Select unit')}
                    noErrorMessage
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FormFields.Input
                    control={control}
                    name='name'
                    withoutAsterisk
                    rules={{ required: true }}
                    label={t('Assessment name')}
                    placeholder={t('Enter Assessment name')}
                    noErrorMessage
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Crop residue management')}
              <InfoPopup
                style={{ minWidth: 400 }}
                content={t(`Crop residues in this section are the plant dry matter which is not used as a product - e.g. straw, stalks, leaf litter etc. If unknown, 
                  a default is estimated based on crop type and yield. Dry matter content of crop residues varies by species; for example - it can be around 90% for cereals and beans.`)}
                styleIcon={{ fontSize: '1.3em' }}
              />
            </div>
          </Header>
          <Segment attached>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FormFields.Input
                    control={control}
                    name='residueAmount'
                    withoutAsterisk
                    label={t('Residue amount')}
                    placeholder={t('Enter residue amount')}
                    rules={{ required: true }}
                    typeInput='controled'
                    noErrorMessage
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FormFields.Select
                    control={control}
                    name='residueAmountUnit'
                    label={t('Residue amount unit')}
                    withoutAsterisk
                    rules={{ required: true }}
                    options={hectarUnitOptions}
                    placeholder={t('Select unit')}
                    noErrorMessage
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <FormFields.Select
                    control={control}
                    name='residueManagementType'
                    label={t('Residue management')}
                    rules={{ required: true }}
                    withoutAsterisk
                    options={residueManagementOptions}
                    placeholder={t('Select residue management type')}
                    noErrorMessage
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          <CoProducts />
        </Grid.Column>
      </Grid.Row>

    </Grid>
  )
}

export default CropDetails
